import { CSVData, VdpTemplate } from '../types';
import { autoSyncCsvForTemplate } from './templateCsvMapper';

export type ValidationSeverity = 'error' | 'warning' | 'info';

export type ValidationCategory = 
  | 'structure'
  | 'missing_columns'
  | 'type_mismatch'
  | 'data_format'
  | 'empty_values'
  | 'duplicates'
  | 'encoding';

export interface ValidationIssue {
  id: string;
  severity: ValidationSeverity;
  category: ValidationCategory;
  title: string;
  message: string;
  columnName?: string;
  rowIndices?: number[]; // 0-based
  sampleValues?: string[];
  fixAction?: 'auto_map' | 'add_column' | 'sanitize_type' | 'remove_empty_rows' | 'trim_whitespace' | 'fill_defaults' | 'fix_ean13' | 'deduplicate' | 'clean_headers';
  fixLabel?: string;
}

export interface TemplateVariableValidation {
  variableName: string;
  elementType: string;
  expectedDataType: 'text' | 'number' | 'date' | 'barcode' | 'ean13' | 'upca' | 'url' | 'image';
  status: 'valid' | 'missing_column' | 'type_mismatch' | 'has_empty_cells';
  statusMessage: string;
  missing: boolean;
  mismatchedType: boolean;
  hasEmpty: boolean;
  invalidRowIndices: number[];
  emptyRowIndices: number[];
  invalidSampleValues: string[];
  validSampleValue?: string;
  suggestedFix?: string;
  fixAction?: 'add_column' | 'sanitize_type' | 'fill_defaults' | 'auto_map' | 'fix_ean13';
  fixLabel?: string;
}

export interface ColumnStat {
  name: string;
  inferredType: 'text' | 'number' | 'date' | 'barcode' | 'url' | 'empty' | 'mixed';
  totalCount: number;
  uniqueCount: number;
  emptyCount: number;
  emptyPercentage: number;
  hasDuplicates: boolean;
  duplicateCount: number;
  sampleValues: string[];
  isMapped: boolean;
  mappedElementType?: string;
  invalidFormatCount?: number;
  hasTypeMismatch?: boolean;
}

export interface ValidationReport {
  isValid: boolean; // True if no 'error' severity issues
  hasErrors: boolean;
  hasWarnings: boolean;
  totalRows: number;
  totalCols: number;
  emptyRowsCount: number;
  blankCellsCount: number;
  duplicateKeyCount: number;
  missingMappedColumns: string[];
  mappedColumns: string[];
  unmappedColumns: string[];
  templateVariables: TemplateVariableValidation[];
  cellErrors: Record<string, number[]>; // columnName -> array of rowIndices with error (for red cell highlighting)
  issues: ValidationIssue[];
  columnStats: Record<string, ColumnStat>;
  passedChecks: { id: string; name: string; description: string }[];
  healthScore: number; // 0 to 100
}

/**
 * Validates CSV dataset structure, header integrity, template bindings, and format correctness.
 */
export function validateCsvData(
  csvData: CSVData | null,
  template?: VdpTemplate | null
): ValidationReport {
  const issues: ValidationIssue[] = [];
  const passedChecks: { id: string; name: string; description: string }[] = [];

  if (!csvData || !csvData.headers || csvData.headers.length === 0 || !csvData.rows || csvData.rows.length === 0) {
    return {
      isValid: false,
      hasErrors: true,
      hasWarnings: false,
      totalRows: 0,
      totalCols: 0,
      emptyRowsCount: 0,
      blankCellsCount: 0,
      duplicateKeyCount: 0,
      missingMappedColumns: [],
      mappedColumns: [],
      unmappedColumns: [],
      templateVariables: [],
      cellErrors: {},
      issues: [
        {
          id: 'no_data',
          severity: 'error',
          category: 'structure',
          title: 'No CSV Dataset Provided',
          message: 'Please upload a CSV file or generate a data column before proceeding to design.'
        }
      ],
      columnStats: {},
      passedChecks: [],
      healthScore: 0
    };
  }

  const { headers, rows } = csvData;
  const totalRows = rows.length;
  const totalCols = headers.length;
  const cellErrors: Record<string, number[]> = {};

  // Helper to record a cell-level error for visual red highlighting in data grid
  const recordCellError = (col: string, rowIdx: number) => {
    if (!cellErrors[col]) {
      cellErrors[col] = [];
    }
    if (!cellErrors[col].includes(rowIdx)) {
      cellErrors[col].push(rowIdx);
    }
  };

  // 1. Header Integrity Checks
  const headerMap: Record<string, number> = {};
  const blankHeaders: number[] = [];
  const duplicateHeaders: string[] = [];
  const untrimmedHeaders: string[] = [];
  const specialCharHeaders: string[] = [];

  headers.forEach((h, idx) => {
    const rawH = String(h ?? '');
    if (rawH.trim() === '') {
      blankHeaders.push(idx);
    } else {
      if (rawH !== rawH.trim()) {
        untrimmedHeaders.push(rawH);
      }
      const lower = rawH.trim().toLowerCase();
      if (headerMap[lower] !== undefined) {
        duplicateHeaders.push(rawH.trim());
      }
      headerMap[lower] = (headerMap[lower] || 0) + 1;

      if (/[\n\r\t"[\]{}]/.test(rawH)) {
        specialCharHeaders.push(rawH);
      }
    }
  });

  if (blankHeaders.length > 0) {
    issues.push({
      id: 'blank_headers',
      severity: 'error',
      category: 'structure',
      title: 'Blank Header Name(s) Found',
      message: `${blankHeaders.length} column(s) have empty or missing header names. Every column must have a valid name.`,
      fixAction: 'clean_headers',
      fixLabel: 'Auto-Name Blank Headers'
    });
  }

  if (duplicateHeaders.length > 0) {
    issues.push({
      id: 'duplicate_headers',
      severity: 'error',
      category: 'structure',
      title: 'Duplicate Header Names',
      message: `Found duplicate header columns: [${Array.from(new Set(duplicateHeaders)).join(', ')}]. Column names must be unique.`,
      fixAction: 'clean_headers',
      fixLabel: 'Rename Duplicate Headers'
    });
  }

  if (untrimmedHeaders.length > 0) {
    issues.push({
      id: 'untrimmed_headers',
      severity: 'warning',
      category: 'structure',
      title: 'Whitespace in Column Headers',
      message: `Headers [${untrimmedHeaders.slice(0, 3).join(', ')}] have leading/trailing whitespace which can cause mapping mismatches.`,
      fixAction: 'trim_whitespace',
      fixLabel: 'Trim Header Whitespace'
    });
  }

  if (specialCharHeaders.length > 0) {
    issues.push({
      id: 'special_char_headers',
      severity: 'warning',
      category: 'structure',
      title: 'Special Characters in Headers',
      message: `Headers [${specialCharHeaders.slice(0, 3).join(', ')}] contain quotes, brackets, or control characters.`
    });
  }

  // Check if dataset was parsed into only 1 column because of a mismatched delimiter
  if (totalCols === 1 && totalRows > 0) {
    const singleHeader = headers[0];
    const sampleCells = rows.slice(0, 5).map(r => String(r[singleHeader] || ''));
    const combinedSample = [singleHeader, ...sampleCells].join('\n');

    if (combinedSample.includes('\t')) {
      issues.push({
        id: 'potential_tab_delimiter',
        severity: 'warning',
        category: 'structure',
        title: 'Possible Tab-Delimited (TSV) File',
        message: 'The file loaded as 1 column, but unparsed Tab (\\t) characters were detected. Switch delimiter to Tab.',
        fixAction: 'clean_headers',
        fixLabel: 'Parse as Tab (\\t)'
      });
    } else if (combinedSample.includes(';')) {
      issues.push({
        id: 'potential_semicolon_delimiter',
        severity: 'warning',
        category: 'structure',
        title: 'Possible Semicolon-Delimited File',
        message: 'The file loaded as 1 column, but unparsed Semicolons (;) were detected. Switch delimiter to Semicolon.',
        fixAction: 'clean_headers',
        fixLabel: 'Parse as Semicolon (;)'
      });
    } else if (combinedSample.includes('|')) {
      issues.push({
        id: 'potential_pipe_delimiter',
        severity: 'warning',
        category: 'structure',
        title: 'Possible Pipe-Delimited File',
        message: 'The file loaded as 1 column, but unparsed Pipe (|) characters were detected. Switch delimiter to Pipe.',
        fixAction: 'clean_headers',
        fixLabel: 'Parse as Pipe (|)'
      });
    }
  }

  if (blankHeaders.length === 0 && duplicateHeaders.length === 0) {
    passedChecks.push({
      id: 'headers_valid',
      name: 'Column Headers Structured',
      description: `All ${totalCols} columns have valid, distinct names.`
    });
  }

  // 2. Row Uniformity & Empty Rows
  const emptyRowIndices: number[] = [];
  let totalBlankCells = 0;
  let hasUntrimmedCells = false;

  rows.forEach((row, rIdx) => {
    let isRowEmpty = true;
    headers.forEach(h => {
      const val = row[h];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        isRowEmpty = false;
      } else {
        totalBlankCells++;
      }
      if (typeof val === 'string' && val !== val.trim()) {
        hasUntrimmedCells = true;
      }
    });

    if (isRowEmpty) {
      emptyRowIndices.push(rIdx);
    }
  });

  if (emptyRowIndices.length > 0) {
    issues.push({
      id: 'empty_rows',
      severity: 'warning',
      category: 'empty_values',
      title: 'Completely Empty Records Detected',
      message: `Found ${emptyRowIndices.length} completely blank row(s) (e.g. row ${emptyRowIndices.slice(0, 3).map(i => i + 1).join(', ')}). These will generate blank stickers.`,
      rowIndices: emptyRowIndices,
      fixAction: 'remove_empty_rows',
      fixLabel: 'Remove Blank Rows'
    });
  } else {
    passedChecks.push({
      id: 'no_empty_rows',
      name: 'No Blank Rows',
      description: 'All records contain variable data entries.'
    });
  }

  if (hasUntrimmedCells) {
    issues.push({
      id: 'untrimmed_cells',
      severity: 'info',
      category: 'structure',
      title: 'Untrimmed Cell Values',
      message: 'Some records have extra leading or trailing whitespace.',
      fixAction: 'trim_whitespace',
      fixLabel: 'Trim All Values'
    });
  }

  // 3. Template Bindings, Required Variables & Deep Type Mismatch Scanning
  const dynamicElements = (template?.elements || []).filter(
    el => !el.isStatic && el.columnName && el.columnName.trim() !== '' && el.type !== 'serial'
  );

  const mappedColumns: string[] = [];
  const unmappedColumns: string[] = [];
  const missingMappedColumns: string[] = [];
  const templateVariables: TemplateVariableValidation[] = [];

  // Extract all unique required variables from template dynamic elements
  const templateRequiredCols = Array.from(
    new Set(dynamicElements.map(el => el.columnName!.trim()))
  );

  templateRequiredCols.forEach(col => {
    if (headers.includes(col)) {
      mappedColumns.push(col);
    } else {
      missingMappedColumns.push(col);
    }
  });

  headers.forEach(h => {
    if (!templateRequiredCols.includes(h)) {
      unmappedColumns.push(h);
    }
  });

  // Scan each required template variable for missing columns and data type mismatches
  templateRequiredCols.forEach(reqCol => {
    const el = dynamicElements.find(e => e.columnName?.trim() === reqCol);
    const elType = el?.type || 'text';
    const colLower = reqCol.toLowerCase();

    // Determine expected data type
    let expectedDataType: TemplateVariableValidation['expectedDataType'] = 'text';
    if (elType === 'currency' || colLower.includes('price') || colLower.includes('cost') || colLower.includes('amount') || colLower.includes('mrp')) {
      expectedDataType = 'number';
    } else if (elType === 'ean13' || colLower.includes('ean13') || colLower.includes('ean-13')) {
      expectedDataType = 'ean13';
    } else if (elType === 'upca' || colLower.includes('upca') || colLower.includes('upc-a')) {
      expectedDataType = 'upca';
    } else if (elType === 'barcode' || colLower.includes('barcode') || colLower.includes('code128')) {
      expectedDataType = 'barcode';
    } else if (elType === 'date' || colLower.includes('date') || colLower.includes('exp') || colLower.includes('mfg')) {
      expectedDataType = 'date';
    } else if (elType === 'qr' || colLower.includes('qr') || colLower.includes('link') || colLower.includes('url')) {
      expectedDataType = 'url';
    } else if (elType === 'image' || colLower.includes('image') || colLower.includes('photo') || colLower.includes('logo')) {
      expectedDataType = 'image';
    }

    // Check if column is MISSING
    if (!headers.includes(reqCol)) {
      issues.push({
        id: `missing_col_${reqCol}`,
        severity: 'error',
        category: 'missing_columns',
        title: `Missing Required Column "${reqCol}"`,
        message: `The active template requires column "${reqCol}" for a ${elType.toUpperCase()} element, but this column is missing from your uploaded dataset. Stickers cannot be generated without this variable.`,
        columnName: reqCol,
        fixAction: 'add_column',
        fixLabel: `Auto-Fix: Add Column "${reqCol}"`
      });

      templateVariables.push({
        variableName: reqCol,
        elementType: elType,
        expectedDataType,
        status: 'missing_column',
        statusMessage: `Required column "${reqCol}" is missing from spreadsheet`,
        missing: true,
        mismatchedType: false,
        hasEmpty: true,
        invalidRowIndices: [],
        emptyRowIndices: [],
        invalidSampleValues: [],
        suggestedFix: `Add column "${reqCol}" with auto-generated valid ${expectedDataType} data matching template requirements`,
        fixAction: 'add_column',
        fixLabel: `Auto-Fix: Add Column`
      });
      return;
    }

    // Column is present: Scan rows for type mismatches and empty cells
    const invalidRowIndices: number[] = [];
    const emptyRowIndices: number[] = [];
    const invalidSampleValues: string[] = [];
    let validSample: string | undefined;

    rows.forEach((row, rIdx) => {
      const rawVal = row[reqCol];
      const strVal = rawVal !== undefined && rawVal !== null ? String(rawVal).trim() : '';

      if (strVal === '') {
        emptyRowIndices.push(rIdx);
        recordCellError(reqCol, rIdx);
        return;
      }

      if (!validSample) {
        validSample = strVal;
      }

      let isTypeMismatch = false;

      if (expectedDataType === 'number') {
        // Strip common currency symbols, commas, and whitespace
        const stripped = strVal.replace(/[$₹€£,\s]/g, '');
        if (isNaN(Number(stripped)) || stripped === '' || /[a-df-z]/i.test(stripped)) {
          isTypeMismatch = true;
        }
      } else if (expectedDataType === 'ean13') {
        const digits = strVal.replace(/\D/g, '');
        if (digits.length !== 12 && digits.length !== 13) {
          isTypeMismatch = true;
        }
      } else if (expectedDataType === 'upca') {
        const digits = strVal.replace(/\D/g, '');
        if (digits.length !== 11 && digits.length !== 12) {
          isTypeMismatch = true;
        }
      } else if (expectedDataType === 'date') {
        const isStandardDate = /^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}/.test(strVal) || /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}/.test(strVal);
        const isParsable = !isNaN(Date.parse(strVal)) && strVal.length >= 6 && isNaN(Number(strVal));
        if (!isStandardDate && !isParsable) {
          isTypeMismatch = true;
        }
      } else if (expectedDataType === 'url') {
        // For QR code / URL, warn if it has spaces or unprintable characters
        if (/\s/.test(strVal) && strVal.length > 80) {
          isTypeMismatch = true;
        }
      }

      if (isTypeMismatch) {
        invalidRowIndices.push(rIdx);
        recordCellError(reqCol, rIdx);
        if (invalidSampleValues.length < 3 && !invalidSampleValues.includes(strVal)) {
          invalidSampleValues.push(strVal);
        }
      }
    });

    // Evaluate variable status
    if (invalidRowIndices.length > 0) {
      issues.push({
        id: `type_mismatch_${reqCol}`,
        severity: 'error',
        category: 'type_mismatch',
        title: `Data Type Mismatch in Column "${reqCol}"`,
        message: `Template requires ${expectedDataType.toUpperCase()} for "${reqCol}", but found ${invalidRowIndices.length} mismatched record(s) (e.g. "${invalidSampleValues.join('", "')}"). These will render broken or invalid on printed stickers.`,
        columnName: reqCol,
        rowIndices: invalidRowIndices,
        sampleValues: invalidSampleValues,
        fixAction: expectedDataType === 'ean13' ? 'fix_ean13' : 'sanitize_type',
        fixLabel: `Auto-Fix: Sanitize "${reqCol}" to ${expectedDataType.toUpperCase()}`
      });

      templateVariables.push({
        variableName: reqCol,
        elementType: elType,
        expectedDataType,
        status: 'type_mismatch',
        statusMessage: `${invalidRowIndices.length} record(s) contain invalid data (expected ${expectedDataType.toUpperCase()})`,
        missing: false,
        mismatchedType: true,
        hasEmpty: emptyRowIndices.length > 0,
        invalidRowIndices,
        emptyRowIndices,
        invalidSampleValues,
        validSampleValue: validSample,
        suggestedFix: `Clean and convert all ${invalidRowIndices.length} invalid values into standard ${expectedDataType.toUpperCase()} format`,
        fixAction: expectedDataType === 'ean13' ? 'fix_ean13' : 'sanitize_type',
        fixLabel: `Auto-Fix Type`
      });
    } else if (emptyRowIndices.length > 0) {
      const isCritical = emptyRowIndices.length === totalRows;
      templateVariables.push({
        variableName: reqCol,
        elementType: elType,
        expectedDataType,
        status: 'has_empty_cells',
        statusMessage: isCritical 
          ? `Column "${reqCol}" is completely blank across all records`
          : `Column "${reqCol}" has ${emptyRowIndices.length} blank cell(s) (${Math.round((emptyRowIndices.length / totalRows) * 100)}%)`,
        missing: false,
        mismatchedType: false,
        hasEmpty: true,
        invalidRowIndices: [],
        emptyRowIndices,
        invalidSampleValues: [],
        validSampleValue: validSample,
        suggestedFix: `Fill blank cells with standard ${expectedDataType.toUpperCase()} sample values to prevent blank stickers`,
        fixAction: 'fill_defaults',
        fixLabel: `Auto-Fix: Fill Blanks`
      });
    } else {
      templateVariables.push({
        variableName: reqCol,
        elementType: elType,
        expectedDataType,
        status: 'valid',
        statusMessage: `Valid ${expectedDataType.toUpperCase()} data matched across all ${totalRows} records`,
        missing: false,
        mismatchedType: false,
        hasEmpty: false,
        invalidRowIndices: [],
        emptyRowIndices: [],
        invalidSampleValues: [],
        validSampleValue: validSample
      });
    }
  });

  if (missingMappedColumns.length === 0 && templateRequiredCols.length > 0) {
    passedChecks.push({
      id: 'all_mapped_present',
      name: 'Template Columns Mapped',
      description: `All ${templateRequiredCols.length} dynamic template element bindings match CSV columns.`
    });
  }

  // 4. Column-by-Column Deep Inspection & Format Checks
  const columnStats: Record<string, ColumnStat> = {};
  let duplicateKeyCount = 0;

  headers.forEach(header => {
    let emptyCount = 0;
    let numberCount = 0;
    let dateCount = 0;
    let barcodeCount = 0;
    let urlCount = 0;
    let textCount = 0;
    let invalidBarcodeCount = 0;

    const valueSet = new Set<string>();
    const duplicateSet = new Set<string>();
    const sampleValues: string[] = [];

    const isBarcodeColumn = /barcode|ean|upc|code128|sku|code/i.test(header);
    const isEan13Column = /ean13|ean-13|ean/i.test(header);
    const isUrlColumn = /url|link|qr|web|http/i.test(header);
    const isDateColumn = /date|time|dob|exp|created/i.test(header);
    const isNumberColumn = /price|cost|amount|qty|quantity|num|id|serial/i.test(header);

    const mappedEl = dynamicElements.find(e => e.columnName?.trim() === header);

    rows.forEach((row) => {
      const rawVal = row[header];
      const strVal = rawVal !== undefined && rawVal !== null ? String(rawVal).trim() : '';

      if (strVal === '') {
        emptyCount++;
      } else {
        if (valueSet.has(strVal)) {
          duplicateSet.add(strVal);
        } else {
          valueSet.add(strVal);
          if (sampleValues.length < 4) {
            sampleValues.push(strVal);
          }
        }

        // Format detection
        if (/^https?:\/\/[^\s]+$/i.test(strVal) || strVal.includes('http://') || strVal.includes('https://')) {
          urlCount++;
        } else if (/^\d{12,13}$/.test(strVal)) {
          barcodeCount++;
        } else if (/^\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}/.test(strVal) || (!isNaN(Date.parse(strVal)) && strVal.length > 5 && isNaN(Number(strVal)))) {
          dateCount++;
        } else if (!isNaN(Number(strVal)) && strVal !== '') {
          numberCount++;
        } else {
          textCount++;
        }

        // Barcode-specific check
        if (mappedEl?.type === 'ean13' || isEan13Column) {
          const digitsOnly = strVal.replace(/\D/g, '');
          if (digitsOnly.length !== 12 && digitsOnly.length !== 13) {
            invalidBarcodeCount++;
          }
        } else if (mappedEl?.type === 'upca') {
          const digitsOnly = strVal.replace(/\D/g, '');
          if (digitsOnly.length !== 11 && digitsOnly.length !== 12) {
            invalidBarcodeCount++;
          }
        }
      }
    });

    const populatedCount = totalRows - emptyCount;
    let inferredType: ColumnStat['inferredType'] = 'text';

    if (populatedCount === 0) {
      inferredType = 'empty';
    } else if (urlCount / populatedCount > 0.6 || isUrlColumn) {
      inferredType = 'url';
    } else if (barcodeCount / populatedCount > 0.6 || isBarcodeColumn) {
      inferredType = 'barcode';
    } else if (dateCount / populatedCount > 0.6 || isDateColumn) {
      inferredType = 'date';
    } else if (numberCount / populatedCount > 0.6 || isNumberColumn) {
      inferredType = 'number';
    } else if (textCount / populatedCount > 0.6) {
      inferredType = 'text';
    } else {
      inferredType = 'mixed';
    }

    const hasDuplicates = duplicateSet.size > 0;
    if (hasDuplicates && (isNumberColumn || /id|serial|sn|key|sku/i.test(header))) {
      duplicateKeyCount += duplicateSet.size;
    }

    const emptyPercentage = Math.round((emptyCount / totalRows) * 100);
    const varValidation = templateVariables.find(v => v.variableName === header);

    columnStats[header] = {
      name: header,
      inferredType,
      totalCount: totalRows,
      uniqueCount: valueSet.size,
      emptyCount,
      emptyPercentage,
      hasDuplicates,
      duplicateCount: duplicateSet.size,
      sampleValues,
      isMapped: mappedColumns.includes(header),
      mappedElementType: mappedEl?.type,
      invalidFormatCount: invalidBarcodeCount,
      hasTypeMismatch: varValidation?.status === 'type_mismatch'
    };

    // Warn if mapped column has empty values
    if (mappedColumns.includes(header) && emptyCount > 0) {
      issues.push({
        id: `empty_mapped_${header}`,
        severity: emptyCount === totalRows ? 'error' : 'warning',
        category: 'empty_values',
        title: `Blank Values in Mapped Column "${header}"`,
        message: emptyCount === totalRows 
          ? `Column "${header}" is mapped to a template element but is 100% empty across all records.`
          : `Column "${header}" has ${emptyCount} blank record(s) (${emptyPercentage}%). Those stickers may print with missing text or codes.`,
        columnName: header,
        fixAction: 'fill_defaults',
        fixLabel: `Auto-Fill Blank Cells in ${header}`
      });
    }

    // Barcode invalid format warnings
    if (invalidBarcodeCount > 0 && !issues.some(i => i.id === `type_mismatch_${header}`)) {
      issues.push({
        id: `invalid_barcode_${header}`,
        severity: 'warning',
        category: 'data_format',
        title: `Invalid Barcode Length in "${header}"`,
        message: `Found ${invalidBarcodeCount} value(s) in "${header}" that are not standard 12/13 digits for EAN/UPC barcodes.`,
        columnName: header,
        sampleValues: sampleValues.slice(0, 3),
        fixAction: 'fix_ean13',
        fixLabel: `Auto-Fix EAN-13 Checksums in ${header}`
      });
    }
  });

  // Check for duplicate IDs in key identifier columns
  if (duplicateKeyCount > 0) {
    issues.push({
      id: 'duplicate_ids',
      severity: 'info',
      category: 'duplicates',
      title: 'Duplicate Identifier Values Found',
      message: `Detected ${duplicateKeyCount} duplicate value(s) in serial/ID columns. Verify if unique barcodes or passes are required.`
    });
  }

  if (totalRows > 0 && totalBlankCells === 0) {
    passedChecks.push({
      id: 'full_density',
      name: '100% Data Density',
      description: 'Zero blank cells detected across all records and columns.'
    });
  }

  const hasErrors = issues.some(i => i.severity === 'error');
  const hasWarnings = issues.some(i => i.severity === 'warning');

  // Compute Health Score (0 - 100)
  let healthScore = 100;
  issues.forEach(i => {
    if (i.severity === 'error') healthScore -= 30;
    else if (i.severity === 'warning') healthScore -= 10;
    else if (i.severity === 'info') healthScore -= 3;
  });
  healthScore = Math.max(0, Math.min(100, healthScore));

  return {
    isValid: !hasErrors,
    hasErrors,
    hasWarnings,
    totalRows,
    totalCols,
    emptyRowsCount: emptyRowIndices.length,
    blankCellsCount: totalBlankCells,
    duplicateKeyCount,
    missingMappedColumns,
    mappedColumns,
    unmappedColumns,
    templateVariables,
    cellErrors,
    issues,
    columnStats,
    passedChecks,
    healthScore
  };
}

/**
 * Calculates EAN-13 checksum for 12 digits
 */
function calculateEan13(base12: string): string {
  const clean = base12.replace(/\D/g, '').padEnd(12, '0').substring(0, 12);
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(clean[i], 10) * (i % 2 === 0 ? 1 : 3);
  }
  const checksum = (10 - (sum % 10)) % 10;
  return clean + checksum;
}

/**
 * Generates an authentic, typed sample value for an element based on its data type or column name
 */
export function generateSampleValueForElement(
  colName: string, 
  el?: { type?: string; columnName?: string; content?: string } | null, 
  idx: number = 0
): string {
  const colLower = colName.toLowerCase();
  const elType = el?.type || 'text';

  if (elType === 'barcode' || colLower.includes('barcode') || colLower.includes('ean') || colLower.includes('upc')) {
    const base12 = `890100${(idx + 1).toString().padStart(6, '0')}`;
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const digit = parseInt(base12[i], 10);
      sum += (i % 2 === 0) ? digit : digit * 3;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return `${base12}${checkDigit}`;
  }

  if (elType === 'qrcode' || colLower.includes('url') || colLower.includes('link') || colLower.includes('web')) {
    return `https://vdp.app/p/${colLower.replace(/[^a-z0-9]/g, '') || 'item'}/${idx + 1}`;
  }

  if (elType === 'currency' || colLower.includes('price') || colLower.includes('cost') || colLower.includes('amount') || colLower.includes('mrp')) {
    return (19.99 + idx * 5).toFixed(2);
  }

  if (elType === 'date' || colLower.includes('date') || colLower.includes('mfg') || colLower.includes('exp')) {
    const d = new Date();
    d.setDate(d.getDate() + idx);
    return d.toISOString().split('T')[0];
  }

  if (colLower.includes('sku') || colLower.includes('code') || colLower.includes('item_no')) {
    return `SKU-${1000 + idx + 1}`;
  }

  if (colLower.includes('qty') || colLower.includes('count') || colLower.includes('stock')) {
    return String(10 + idx);
  }

  if (colLower.includes('title') || colLower.includes('name') || colLower.includes('product')) {
    return `Item Product ${idx + 1}`;
  }

  if (el?.content && el.content.trim() !== '') {
    return `${el.content.trim()} ${idx + 1}`;
  }

  return `${colName}_${(idx + 1).toString().padStart(3, '0')}`;
}

/**
 * Applies quick fix transformations to repair CSV dataset
 */
export function applyCsvQuickFix(
  csvData: CSVData,
  issue: ValidationIssue,
  template?: VdpTemplate | null
): CSVData {
  if (!csvData) return csvData;

  const headers = [...csvData.headers];
  let rows = csvData.rows.map(r => ({ ...r }));

  switch (issue.fixAction) {
    case 'auto_map': {
      if (template) {
        return autoSyncCsvForTemplate(template, csvData);
      }
      break;
    }

    case 'add_column': {
      if (issue.columnName) {
        const col = issue.columnName;
        // Check if there is an unmapped column with a similar name (alias matching)
        const targetClean = col.toLowerCase().replace(/[^a-z0-9]/g, '');
        const aliasHeader = headers.find(h => {
          const hClean = h.toLowerCase().replace(/[^a-z0-9]/g, '');
          return hClean === targetClean || 
                 (targetClean.includes('price') && hClean.includes('cost')) ||
                 (targetClean.includes('barcode') && (hClean.includes('ean') || hClean.includes('upc') || hClean.includes('sku'))) ||
                 (targetClean.includes('product') && hClean.includes('item'));
        });

        if (aliasHeader && aliasHeader !== col) {
          // Rename alias header to match template required column
          const newHeaders = headers.map(h => h === aliasHeader ? col : h);
          const newRows = rows.map(r => {
            const updated = { ...r };
            updated[col] = r[aliasHeader];
            delete updated[aliasHeader];
            return updated;
          });
          return { headers: newHeaders, rows: newRows };
        }

        // Otherwise append column and populate valid typed sample data
        if (!headers.includes(col)) {
          headers.push(col);
        }
        const matchingEl = template?.elements.find(e => e.columnName?.trim() === col);
        rows = rows.map((r, idx) => {
          return {
            ...r,
            [col]: r[col] !== undefined && r[col] !== null && String(r[col]).trim() !== ''
              ? r[col]
              : (matchingEl ? generateSampleValueForElement(col, matchingEl, idx) : `${col}_${idx + 1}`)
          };
        });
      }
      break;
    }

    case 'sanitize_type': {
      if (issue.columnName) {
        const col = issue.columnName;
        const matchingEl = template?.elements.find(e => e.columnName?.trim() === col);
        const elType = matchingEl?.type || 'text';
        const colLower = col.toLowerCase();

        const isNumeric = elType === 'currency' || colLower.includes('price') || colLower.includes('cost') || colLower.includes('amount');
        const isEan13 = elType === 'ean13' || colLower.includes('ean13') || colLower.includes('ean');
        const isDate = elType === 'date' || colLower.includes('date') || colLower.includes('exp');
        const isUrl = elType === 'qr' || colLower.includes('url') || colLower.includes('link');

        rows = rows.map((r, idx) => {
          const rawVal = r[col];
          const strVal = rawVal !== undefined && rawVal !== null ? String(rawVal).trim() : '';

          if (isNumeric) {
            const digits = strVal.replace(/[^0-9.]/g, '');
            const parsed = parseFloat(digits);
            return {
              ...r,
              [col]: !isNaN(parsed) && digits !== '' ? parsed.toFixed(2) : (29.99 + (idx * 5.00)).toFixed(2)
            };
          } else if (isEan13) {
            const digits = strVal.replace(/\D/g, '');
            const base12 = (digits.length >= 12 ? digits : (890123456000 + idx + 1).toString()).slice(0, 12);
            return {
              ...r,
              [col]: calculateEan13(base12)
            };
          } else if (isDate) {
            const dateObj = new Date();
            dateObj.setDate(dateObj.getDate() + (idx * 7) + 30);
            return {
              ...r,
              [col]: dateObj.toISOString().split('T')[0]
            };
          } else if (isUrl) {
            return {
              ...r,
              [col]: strVal.startsWith('http') ? strVal.replace(/\s+/g, '-') : `https://vdp.app/item/${colLower.replace(/[^a-z0-9]/g, '')}-${idx + 1}`
            };
          }

          return r;
        });
      }
      break;
    }

    case 'remove_empty_rows': {
      rows = rows.filter(row => {
        return headers.some(h => {
          const val = row[h];
          return val !== undefined && val !== null && String(val).trim() !== '';
        });
      });
      break;
    }

    case 'trim_whitespace': {
      const cleanHeaders = headers.map(h => String(h || '').trim() || 'Column');
      const cleanRows = rows.map(row => {
        const newRow: Record<string, any> = {};
        headers.forEach((h, idx) => {
          const cleanH = cleanHeaders[idx];
          const val = row[h];
          newRow[cleanH] = typeof val === 'string' ? val.trim() : val;
        });
        return newRow;
      });
      return {
        headers: cleanHeaders,
        rows: cleanRows
      };
    }

    case 'clean_headers': {
      const seen: Record<string, number> = {};
      const cleanHeaders = headers.map((h, i) => {
        let base = String(h || '').trim();
        if (!base) base = `Column_${i + 1}`;
        if (seen[base] !== undefined) {
          seen[base]++;
          return `${base}_${seen[base]}`;
        }
        seen[base] = 1;
        return base;
      });

      const cleanRows = rows.map(row => {
        const newRow: Record<string, any> = {};
        headers.forEach((oldH, i) => {
          newRow[cleanHeaders[i]] = row[oldH];
        });
        return newRow;
      });

      return {
        headers: cleanHeaders,
        rows: cleanRows
      };
    }

    case 'fill_defaults': {
      if (issue.columnName) {
        const col = issue.columnName;
        const matchingEl = template?.elements.find(e => e.columnName?.trim() === col);
        rows = rows.map((row, idx) => {
          if (row[col] === undefined || row[col] === null || String(row[col]).trim() === '') {
            return {
              ...row,
              [col]: matchingEl ? generateSampleValueForElement(col, matchingEl, idx) : `${col}_${(idx + 1).toString().padStart(4, '0')}`
            };
          }
          return row;
        });
      }
      break;
    }

    case 'fix_ean13': {
      if (issue.columnName) {
        const col = issue.columnName;
        rows = rows.map((row, idx) => {
          const val = String(row[col] || '');
          const digits = val.replace(/\D/g, '');
          const base12 = (digits.length >= 12 ? digits : (890100000000 + idx + 1).toString()).substring(0, 12);
          return {
            ...row,
            [col]: calculateEan13(base12)
          };
        });
      }
      break;
    }

    default:
      break;
  }

  return {
    headers,
    rows
  };
}

/**
 * Batch cleans and repairs common dataset anomalies in 1 click
 */
export function autoCleanAllIssues(
  csvData: CSVData,
  template?: VdpTemplate | null
): CSVData {
  if (!csvData) return csvData;

  // 1. Clean headers & trim whitespace
  const seen: Record<string, number> = {};
  const cleanHeaders = csvData.headers.map((h, i) => {
    let base = String(h || '').trim();
    if (!base) base = `Column_${i + 1}`;
    if (seen[base] !== undefined) {
      seen[base]++;
      return `${base}_${seen[base]}`;
    }
    seen[base] = 1;
    return base;
  });

  // 2. Remove empty rows and trim cell values
  let cleanRows = csvData.rows
    .map(row => {
      const newRow: Record<string, any> = {};
      csvData.headers.forEach((oldH, i) => {
        const val = row[oldH];
        newRow[cleanHeaders[i]] = typeof val === 'string' ? val.trim() : val;
      });
      return newRow;
    })
    .filter(row => {
      return cleanHeaders.some(h => {
        const val = row[h];
        return val !== undefined && val !== null && String(val).trim() !== '';
      });
    });

  if (cleanRows.length === 0) {
    cleanRows = [{ [cleanHeaders[0] || 'Item']: 'Sample_001' }];
  }

  let result: CSVData = {
    headers: cleanHeaders,
    rows: cleanRows
  };

  // 3. Auto-sync missing template columns if template provided
  if (template) {
    result = autoSyncCsvForTemplate(template, result);

    // 4. Sanitize type mismatches on all required template columns
    const dynamicElements = template.elements.filter(
      el => !el.isStatic && el.columnName && el.columnName.trim() !== '' && el.type !== 'serial'
    );

    dynamicElements.forEach(el => {
      const col = el.columnName!.trim();
      const colLower = col.toLowerCase();
      const elType = el.type;

      const isNumeric = elType === 'currency' || colLower.includes('price') || colLower.includes('cost') || colLower.includes('amount');
      const isEan13 = elType === 'ean13' || colLower.includes('ean13') || colLower.includes('ean');
      const isDate = elType === 'date' || colLower.includes('date') || colLower.includes('exp');
      const isUrl = elType === 'qr' || colLower.includes('url') || colLower.includes('link');

      result.rows = result.rows.map((row, idx) => {
        const val = row[col];
        const strVal = val !== undefined && val !== null ? String(val).trim() : '';

        if (isNumeric) {
          const stripped = strVal.replace(/[$₹€£,\s]/g, '');
          if (isNaN(Number(stripped)) || stripped === '' || /[a-df-z]/i.test(stripped)) {
            const digits = strVal.replace(/[^0-9.]/g, '');
            const parsed = parseFloat(digits);
            return {
              ...row,
              [col]: !isNaN(parsed) && digits !== '' ? parsed.toFixed(2) : (24.99 + (idx * 5)).toFixed(2)
            };
          }
        } else if (isEan13) {
          const digits = strVal.replace(/\D/g, '');
          if (digits.length !== 12 && digits.length !== 13) {
            const base12 = (digits.length >= 12 ? digits : (890123456000 + idx + 1).toString()).slice(0, 12);
            return {
              ...row,
              [col]: calculateEan13(base12)
            };
          }
        } else if (isDate) {
          const isStandardDate = /^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}/.test(strVal) || /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}/.test(strVal);
          if (!isStandardDate && (isNaN(Date.parse(strVal)) || strVal.length < 6)) {
            const d = new Date();
            d.setDate(d.getDate() + (idx * 7) + 30);
            return {
              ...row,
              [col]: d.toISOString().split('T')[0]
            };
          }
        } else if (isUrl) {
          if (!strVal || strVal.length < 4) {
            return {
              ...row,
              [col]: `https://vdp.app/track/${colLower.replace(/[^a-z0-9]/g, '')}-${idx + 1}`
            };
          }
        }

        // Fill blanks in mapped variables
        if (strVal === '') {
          return {
            ...row,
            [col]: generateSampleValueForElement(col, el, idx)
          };
        }

        return row;
      });
    });
  }

  return result;
}
